import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import SiteReadOnlyBanner from '../../components/SiteReadOnlyBanner';
import { siteService } from '../../services/siteService';
import { stripeService } from '../../services/stripeService';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeEnum } from '../../utils/normalizeEnum';
import { sanitizeError } from '../../utils/sanitizeError';
import { isSiteReadOnlyStatus } from '../../hooks/useSiteReadOnly';

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Complete' },
  { value: 'archived', label: 'Archived' },
];

const STRIPE_MIN_QUANTITY = 2;

const countActiveSites = async (companyId) => {
  if (!companyId) return 0;
  const { count } = await supabase
    ?.from('sites')
    ?.select('id', { count: 'exact', head: true })
    ?.eq('company_id', companyId)
    ?.eq('status', 'active');
  return count || 0;
};

const SiteSettings = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [formData, setFormData] = useState(null);
  const [originalStatus, setOriginalStatus] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [confirmTransition, setConfirmTransition] = useState(null);

  useEffect(() => {
    if (!siteId) return;
    const load = async () => {
      setLoading(true);
      const { data, error: fetchError } = await siteService?.getSiteById(siteId);
      if (fetchError || !data) {
        setError(fetchError?.message || 'Site not found');
      } else {
        setFormData({
          site_name: data?.site_name || '',
          site_reference: data?.site_reference || '',
          start_date: data?.start_date || '',
          end_date: data?.end_date || '',
          address_line1: data?.address_line1 || '',
          address_line2: data?.address_line2 || '',
          city: data?.city || '',
          county: data?.county || '',
          postcode: data?.postcode || '',
          client_name: data?.client_name || '',
          client_contact_person: data?.client_contact_person || '',
          client_email: data?.client_email || '',
          client_phone: data?.client_phone || '',
          status: data?.status || 'planning',
        });
        setOriginalStatus(data?.status || 'planning');
        setCompanyId(data?.company_id || null);
      }
      setLoading(false);
    };
    load();
  }, [siteId]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSuccessMsg('');
  };

  // Recompute active-site count and push to Stripe. Billing floor is
  // STRIPE_MIN_QUANTITY regardless of how few sites are active, because
  // pricing requires a minimum subscription. The edge function also
  // enforces this, so this is belt-and-braces.
  const syncStripeQuantity = async () => {
    if (!companyId) return;
    const activeCount = await countActiveSites(companyId);
    const quantity = Math.max(STRIPE_MIN_QUANTITY, activeCount);
    const { error: syncErr } = await stripeService?.updateSubscription(companyId, quantity);
    if (syncErr) {
      // Non-fatal for the save itself — surface but keep the DB change.
      // sanitizeError translates known Stripe error shapes (e.g. "No such
      // customer") and lets card-decline messages pass through verbatim.
      // Per card hsGzqWrI.
      console.error('[site-settings] Stripe sync failed:', syncErr);
      setError(`Site saved, but billing sync failed: ${sanitizeError(syncErr?.message, 'please contact support if this continues')}`);
    }
  };

  const performSave = async (nextStatus) => {
    setSaving(true);
    setError('');
    setSuccessMsg('');

    const normalizedStatus = normalizeEnum(nextStatus);
    const { error: saveError } = await siteService?.updateSite(siteId, {
      ...formData,
      status: normalizedStatus,
    });

    if (saveError) {
      setError(saveError?.message || 'Failed to save changes');
      setSaving(false);
      return;
    }

    // Only touch Stripe when the active/inactive state actually flips.
    const wasActive = originalStatus === 'active';
    const isActive = normalizedStatus === 'active';
    if (wasActive !== isActive) {
      await syncStripeQuantity();
    }

    setOriginalStatus(normalizedStatus);
    setFormData(prev => ({ ...prev, status: normalizedStatus }));
    setSuccessMsg(
      isSiteReadOnlyStatus(normalizedStatus)
        ? `Site marked as ${normalizedStatus}. Billing updated.`
        : 'Site settings saved successfully'
    );
    setSaving(false);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!formData?.site_name?.trim()) { setError('Site name is required'); return; }
    if (!formData?.address_line1?.trim()) { setError('Address is required'); return; }
    if (!formData?.city?.trim()) { setError('City is required'); return; }
    if (!formData?.postcode?.trim()) { setError('Postcode is required'); return; }
    if (!formData?.start_date) { setError('Start date is required'); return; }
    if (!formData?.end_date) { setError('End date is required'); return; }
    if (!formData?.client_name?.trim()) { setError('Client name is required'); return; }
    if (!formData?.client_contact_person?.trim()) { setError('Client contact person is required'); return; }
    if (!formData?.client_email?.trim()) { setError('Client email is required'); return; }
    if (!formData?.client_phone?.trim()) { setError('Client phone is required'); return; }

    const nextStatus = normalizeEnum(formData?.status);
    const movingToReadOnly =
      !isSiteReadOnlyStatus(originalStatus) && isSiteReadOnlyStatus(nextStatus);

    if (movingToReadOnly) {
      setConfirmTransition(nextStatus);
      return;
    }

    await performSave(nextStatus);
  };

  const handleReactivate = async () => {
    setFormData(prev => ({ ...prev, status: 'active' }));
    await performSave('active');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  if (error && !formData) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-4 py-12">
            <div className="bg-card border border-border rounded-xl p-8 text-center">
              <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground mb-2">Could not load site</p>
              <p className="text-xs text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" size="sm" onClick={() => navigate(-1)}>Go back</Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const currentlyReadOnly = isSiteReadOnlyStatus(originalStatus);

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Site Settings — SiteProof</title></Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate(`/sites/${siteId}`)} className="w-9 h-9 rounded-lg border border-border hover:bg-muted flex items-center justify-center transition-colors">
              <Icon name="ArrowLeft" size={16} className="text-muted-foreground" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground font-heading">Site Settings</h1>
              <p className="text-sm text-muted-foreground">{formData?.site_name}</p>
            </div>
          </div>

          {currentlyReadOnly && (
            <div className="mb-6 space-y-3">
              <SiteReadOnlyBanner status={originalStatus} />
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={handleReactivate}
                disabled={saving}
                className="w-full sm:w-auto"
              >
                <Icon name="RefreshCw" size={14} className="mr-1.5" />
                {saving ? 'Reactivating…' : 'Reactivate site'}
              </Button>
            </div>
          )}

          {/* Success banner */}
          {successMsg && (
            <div className="mb-6 p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-2">
              <Icon name="CheckCircle2" size={16} className="text-emerald-600" />
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{successMsg}</p>
            </div>
          )}

          {/* Error banner */}
          {error && formData && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
              <p className="text-sm font-medium text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            {/* Site Details */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building2" size={16} className="text-muted-foreground" />
                Site Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Site Name *</label>
                  <input name="site_name" value={formData?.site_name} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Site Reference / Code</label>
                  <input name="site_reference" value={formData?.site_reference} onChange={handleChange} className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Start Date *</label>
                  <input name="start_date" type="date" value={formData?.start_date} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">End Date *</label>
                  <input name="end_date" type="date" value={formData?.end_date} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground mb-1">Status</label>
                  <select name="status" value={formData?.status} onChange={handleChange} className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent">
                    {STATUS_OPTIONS?.map(opt => (
                      <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Complete and Archived sites become read-only and stop being billed. You can reactivate at any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                Address
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground mb-1">Address Line 1 *</label>
                  <input name="address_line1" value={formData?.address_line1} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-foreground mb-1">Address Line 2</label>
                  <input name="address_line2" value={formData?.address_line2} onChange={handleChange} className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">City *</label>
                  <input name="city" value={formData?.city} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">County</label>
                  <input name="county" value={formData?.county} onChange={handleChange} className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Postcode *</label>
                  <input name="postcode" value={formData?.postcode} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="bg-card border border-border rounded-xl p-5">
              <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Users" size={16} className="text-muted-foreground" />
                Client Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Client / Company Name *</label>
                  <input name="client_name" value={formData?.client_name} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Contact Person *</label>
                  <input name="client_contact_person" value={formData?.client_contact_person} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Email *</label>
                  <input name="client_email" type="email" value={formData?.client_email} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Phone *</label>
                  <input name="client_phone" type="tel" value={formData?.client_phone} onChange={handleChange} required className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent" />
                </div>
              </div>
            </div>

            {/* Save */}
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" type="button" onClick={() => navigate(`/sites/${siteId}`)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </div>
      </main>

      {/* Confirmation dialog for active → read-only transitions */}
      {confirmTransition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmTransition(null)}>
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e?.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={18} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">
                    Mark site as {confirmTransition === 'completed' ? 'complete' : 'archived'}?
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    This site will become read-only and will no longer be billed. Are you sure?
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" type="button" onClick={() => setConfirmTransition(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  type="button"
                  onClick={async () => {
                    const status = confirmTransition;
                    setConfirmTransition(null);
                    await performSave(status);
                  }}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Yes, proceed'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteSettings;
